import { Col, Row, message } from "antd";
import { Form, Formik } from "formik";
import React, { useEffect, useState } from "react";
import { LoginAPI } from "../../../api/LoginAPI";
import { apiLinkConstants } from "../../../constants";
import { UserHelpers } from "../../../helpers/Helpers";
import { AttachmentTargetType } from "../../../model/AttachmentTargetType";
import { HTMLProps } from "../../../model/HTMLProps";
import { User } from "../../../user/User";
import { Api, ApiMethod } from "../ApiLinkedButton";
import { DeleteIcon } from "../Delete.Icon";
import { ApiLinkedButtonV3 } from "../Form/ApiLinked.button.V3";
import { Loader } from "../Loader.component";
import { MarkdownView } from "../MarkdownView";
import { TimeAgoView } from "../TimeAgoView";
import { FormikSwitchInput } from "../input/FormikSwitch.input";
import { MarkdownFormikInput } from "../input/Markdown.FormikInput";
import { Center } from "../layout/Center.component";
import { CommentsAPI } from "./Comments.api";
import { Comment } from "./Comments.model";



interface CommentsComponentProps {
  targetType: AttachmentTargetType;
  targetId: string;
  dl?: string;
  loggedInuser?: User;
  hideInput?: boolean;
}

export function CommentsComponent(props: CommentsComponentProps) {
  let [comments, setComments] = useState<Comment[] | undefined>(undefined);

  function fetchComments(): Promise<any> {
    return CommentsAPI.getComments(
      props.targetType,
      props.targetId,
      0,
      100
    ).then((commentsResponse) => {
      setComments(commentsResponse);
    });
  }

  useEffect(() => {
    fetchComments();
  }, [props.targetType, props.targetId]);

  function refreshComments(): Promise<any> {
    return fetchComments();
  }

  return (
    <div className="CommentsComponent">
      <div className="h6">{props.dl}</div>
      <div className="">
        {props.loggedInuser && !props.hideInput && (
          <>
            <AddCommentComponent
              {...props}
              addCommentSuccessCallback={refreshComments}
            />
          </>
        )}

        {!props.loggedInuser && !props.hideInput && (
          <>
            <a href={LoginAPI.loginUrl()}>Login</a> to add comment!
          </>
        )}

        {comments && (
          <>
            <div className="mt-4">
              <CommentsListComponent
                comments={comments}
                targetType={props.targetType}
                targetId={props.targetId}
                loggedInuser={props.loggedInuser}
                deleteCommentSuccessCallback={refreshComments}
              />
            </div>
          </>
        )}

        {!comments && (
          <>
            <div className="mt-4">
              <Center>
                <Loader />
              </Center>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

interface CommentsListItemComponentProps
  extends CommentsComponentProps,
  HTMLProps {
  deleteCommentSuccessCallback: () => Promise<void>;
  comment: Comment;
  loggedInuser?: User;
}
function CommentsListItemComponent(props: CommentsListItemComponentProps) {
  let [showReplies, setShowReplies] = useState(false);
  let [isReplying, setIsReplying] = useState(false);
  let [isEditing, setIsEditing] = useState(false);
  let isReply = props.targetType === AttachmentTargetType.COMMENT;

  let deleteApiLink = apiLinkConstants.API_COMMENTS_UPDATE.replaceAll(
    "{targetType}",
    props.targetType
  )
    .replaceAll("{targetId}", props.targetId)
    .replaceAll("{commentId}", props.comment.id + "");

  function handleReplyClick() {
    const nextIsReplying = !isReplying;
    setIsReplying(nextIsReplying);
    if (nextIsReplying) {
      setShowReplies(true);
    } else {
      if (!(props.comment.repliesCount != null && props.comment.repliesCount > 0)) {
        setShowReplies(false);
      }
    }
  }

  function handleViewRepliesClick() {
    setShowReplies(!showReplies);
    if (showReplies) {
      setIsReplying(false);
    }
  }

  return (
    <div
      className={`hstack gap-1 CommentsListItemComponent align-items-start ${props.className}`}
    >
      <div className="mb-auto py-1">
        <img
          width="35"
          src={UserHelpers.getProfilePicUrl(props.comment.postedBy)}
          className="rounded-circle"
        />
      </div>
      <div className="w-100">
        <div className="w-100 bg-light px-2 px-md-3 py-2 rounded-end">
          <Row className="mb-1">
            <Col
              className="review-block-name font-weight-bold"
              flex={"auto"}
              style={{ fontSize: "0.9rem" }}
            >
              {UserHelpers.getFullName(props.comment.postedBy)}
            </Col>
            {props.comment.createdAt && (
              <Col
                className="review-block-date text-right text-muted"
                style={{ fontSize: "0.8rem" }}
              >
                <TimeAgoView datetime={props.comment.createdAt} />
              </Col>
            )}
          </Row>
          <div className="d-flex w-100 justify-content-between">
            <div
              className="review-block-description text-change-line-for-newline w-100"
              style={{ fontSize: "0.95rem" }}
            >
              {isEditing ? (
                <EditCommentComponent
                  comment={props.comment}
                  onCancel={() => setIsEditing(false)}
                  onSuccess={async () => {
                    setIsEditing(false);
                    await props.deleteCommentSuccessCallback();
                  }}
                  loggedInuser={props.loggedInuser}
                />
              ) : (
                <>
                  {props.comment.content && (
                    <>
                      <MarkdownView text={props.comment.content} />
                    </>
                  )}
                </>
              )}
            </div>
            {!isEditing && (
              <div className="float-right mb-auto">
                {props.comment.canEdit && (
                  <ApiLinkedButtonV3
                    api={new Api(deleteApiLink, ApiMethod.DELETE)}
                    buttonProps={{
                      className: "btn-sm btn-light",
                      content: {
                        normal: <DeleteIcon />,
                        submitting: "Deleting",
                      },
                      tooltip: "Delete comment",
                    }}
                    uniqueKey={"comments-" + props.comment.id + "-delete"}
                    successCallback={props.deleteCommentSuccessCallback}
                  />
                )}
              </div>
            )}
          </div>

          {!isEditing && (
            <div
              className="mt-1 mb-1 hstack gap-3 mb-0 pe-auto"
              style={{
                fontSize: "0.8rem",
                cursor: "pointer",
                userSelect: "none",
                color: "#555",
              }}
            >
              {props.comment.repliesCount != null &&
                props.comment.repliesCount > 0 && (
                  <div onClick={handleViewRepliesClick}>
                    {showReplies
                      ? "Hide Replies"
                      : `View ${props.comment.repliesCount} ${
                          props.comment.repliesCount === 1 ? "Reply" : "Replies"
                        }`}
                  </div>
                )}
              <div onClick={handleReplyClick}>
                {isReplying ? "Cancel Reply" : "Reply"}
              </div>
              {props.comment.canEdit && (
                <div onClick={() => setIsEditing(true)}>Edit</div>
              )}
              {props.comment.isEdited && (
                <div className="text-muted" style={{ fontStyle: "italic", cursor: "default" }}>
                  (edited)
                </div>
              )}
            </div>
          )}
        </div>

        {showReplies && (
          <div
            className={`mt-2 ps-0 ps-md-2 ${
              isReplying ||
              (props.comment.repliesCount && props.comment.repliesCount > 0)
                ? "border-start border-1 ps-2 ps-md-3 border-secondary-subtle"
                : ""
            } pb-1`}
          >
            <CommentsComponent
              targetType={AttachmentTargetType.COMMENT}
              targetId={props.comment.id + ""}
              loggedInuser={props.loggedInuser}
              hideInput={!isReplying}
            />
          </div>
        )}
      </div>
    </div>
  );
}

interface EditCommentComponentProps {
  comment: Comment;
  onCancel: () => void;
  onSuccess: () => void;
  loggedInuser?: User;
}

export function EditCommentComponent(props: EditCommentComponentProps) {
  return (
    <div className="EditCommentComponent">
      <Formik
        initialValues={{ content: props.comment.content || "" }}
        onSubmit={(values) => {
          return CommentsAPI.updateComment(props.comment.id!, values).then(
            () => {
              message.success("Updated successfully!");
              props.onSuccess();
            }
          );
        }}
      >
        {(formProps) => (
          <Form>
            <MarkdownFormikInput
              name="content"
              user={props.loggedInuser!}
              minEditorHeight={80}
            />

            <div className="mt-2 hstack gap-2">
              <button
                className="btn btn-sm btn-primary"
                type="submit"
                disabled={formProps.isSubmitting}
              >
                {formProps.isSubmitting ? "Saving..." : "Save"}
              </button>
              <button
                className="btn btn-sm btn-light"
                type="button"
                onClick={props.onCancel}
                disabled={formProps.isSubmitting}
              >
                Cancel
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
}


interface CommentsListComponentProps extends CommentsComponentProps {
  deleteCommentSuccessCallback: () => Promise<any>;
  comments: Comment[];
}

export function CommentsListComponent(props: CommentsListComponentProps) {
  return (
    <div className="CommentsListComponent">
      {props.comments.map((comment, index) => {
        return (
          <CommentsListItemComponent
            key={index}
            className="mb-3"
            comment={comment}
            targetType={props.targetType}
            targetId={props.targetId}
            loggedInuser={props.loggedInuser}
            deleteCommentSuccessCallback={props.deleteCommentSuccessCallback}
          />
        );
      })}
    </div>
  );
}

interface AddCommentComponentProps extends CommentsComponentProps {
  addCommentSuccessCallback: () => void;
}

export function AddCommentComponent(props: AddCommentComponentProps) {
  let initialValues: Comment = { content: "" };
  return (
    <div className="AddCommentComponent">
      <Formik
        initialValues={initialValues}
        onSubmit={(values) => {
          return CommentsAPI.addComment(
            props.targetType,
            props.targetId,
            values
          ).then(() => {
            message.success("Saved successfully!");
            values.content = "";
            props.addCommentSuccessCallback();
          });
        }}
      >
        {(formProps) => (
          <Form>
            <MarkdownFormikInput
              name="content"
              user={props.loggedInuser!}
              minEditorHeight={100}
            />

            <div className="mt-3">
              <Row>
                <Col flex="auto">
                  <button
                    className="btn btn-primary"
                    type="submit"
                    disabled={formProps.isSubmitting}
                  >
                    {formProps.isSubmitting ? "Saving. Please Wait..." : "Send"}
                  </button>
                </Col>
                <Col>
                  <div>
                    <FormikSwitchInput
                      name="isAnon"
                      id={`add-comment-${props.targetType}-${props.targetId}-isAnon`}
                      label="Add as Anonymous?"
                    />
                  </div>
                </Col>
              </Row>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
}
